import { useEffect, useMemo, useState } from 'react'
import Card from '@/components/ui/Card'
import { useAuth } from '@/lib/auth-context'

interface ReimbursementItem {
  id: string
  description: string
  amount_cents: number
  receipt_url?: string
}

interface ReimbursementRequest {
  id: string
  status: string
  total_cents: number
  submitted_at?: string
  memo?: string
  purchase_request_items?: ReimbursementItem[]
}

const STATUS_OPTIONS = ['all', 'draft', 'submitted', 'approved', 'paid', 'rejected']

function formatCurrency(cents: number | null | undefined): string {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((cents || 0) / 100)
}

export default function TeacherReimbursements() {
  const { session } = useAuth()
  const token = session?.access_token
  const [status, setStatus] = useState('all')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [requests, setRequests] = useState<ReimbursementRequest[]>([])
  const [selected, setSelected] = useState<ReimbursementRequest | null>(null)

  const filtered = useMemo(() => {
    if (status === 'all') return requests
    return requests.filter((request) => request.status === status)
  }, [requests, status])

  useEffect(() => {
    if (!token) return
    let ignore = false
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
        const response = await fetch(`${CORE_API}/api/v1/teacher/reimbursements`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const payload = await response.json().catch(() => ({}))
        if (!ignore) {
          if (!response.ok) throw new Error(payload?.error || 'Unable to load reimbursements')
          setRequests(payload.items || [])
        }
      } catch (err) {
        if (!ignore) {
          setError((err as Error)?.message || 'Unable to load reimbursements')
          setRequests([])
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => {
      ignore = true
    }
  }, [token])

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-cocoa">Reimbursements</h1>
        <p className="text-sm text-cocoa/70">Track submitted reimbursements and review line items and receipts.</p>
      </header>

      <Card className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-cocoa/70">
            <span>Status:</span>
            <div className="flex items-center gap-2">
              {STATUS_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option}
                  onClick={() => setStatus(option)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition ${
                    status === option ? 'bg-cocoa text-white shadow' : 'bg-shell text-cocoa/70 hover:bg-shell/80'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-cocoa/70">Loading reimbursements…</p>
        ) : error ? (
          <p className="text-sm text-brick">{error}</p>
        ) : filtered.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-sand">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-cocoa/60">
                  <th className="px-3 py-2">Request</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Total</th>
                  <th className="px-3 py-2">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand text-sm text-cocoa/80">
                {filtered.map((request) => (
                  <tr
                    key={request.id}
                    className="cursor-pointer transition hover:bg-shell/60"
                    onClick={() => setSelected(request)}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{request.id}</td>
                    <td className="px-3 py-2 capitalize">{request.status}</td>
                    <td className="px-3 py-2 font-semibold text-cocoa">{formatCurrency(request.total_cents)}</td>
                    <td className="px-3 py-2">{request.submitted_at ? new Date(request.submitted_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-cocoa/70">No reimbursements found.</p>
        )}
      </Card>

      {selected ? (
        <Card className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-cocoa">Request {selected.id}</h2>
              <p className="text-sm text-cocoa/70">Status: {selected.status}</p>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="rounded-full border border-sand px-4 py-2 text-sm font-semibold text-cocoa hover:bg-shell"
            >
              Close
            </button>
          </div>
          <div className="space-y-3">
            {(selected.purchase_request_items || []).map((item) => (
              <div key={item.id} className="rounded-xl border border-sand p-3 text-sm text-cocoa/80">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-cocoa">{item.description}</span>
                  <span className="font-semibold text-cocoa">{formatCurrency(item.amount_cents)}</span>
                </div>
                {item.receipt_url ? (
                  <a
                    href={item.receipt_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex text-xs font-semibold text-terracotta hover:underline"
                  >
                    View receipt
                  </a>
                ) : null}
              </div>
            ))}
          </div>
          {selected.memo ? (
            <div className="rounded-xl border border-sand bg-shell/40 p-3 text-sm text-cocoa/80">
              <h3 className="text-xs uppercase tracking-wide text-cocoa/60">Memo</h3>
              <p className="mt-1 whitespace-pre-wrap">{selected.memo}</p>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  )
}
